import React from 'react';
import { useNavigate } from 'react-router-dom';

const P = (id: number, w = 1600, h = 900) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=${w}&h=${h}&dpr=1`;

const VALUES = [
  { icon: '🇳🇵', title: 'Nepali-First', desc: "Every model we build is designed around Nepali language and context — not adapted from Western or Indian tools. We understand the grammar, script, idiom, and bureaucratic language of Nepal." },
  { icon: '🔍', title: 'Transparent', desc: "We explain how our AI works, where it might be wrong, and how to verify results. No black boxes handed to organizations that are accountable to citizens." },
  { icon: '♻️', title: 'Accessible', desc: "Great AI should not be a luxury for large corporations. We price for Nepal's market and work with organizations of all sizes, including government agencies on limited budgets." },
  { icon: '⚖️', title: 'Ethical', desc: "We align with Nepal's AI Policy 2082 and global principles for fair, unbiased AI. We decline projects that could harm communities or undermine democratic processes." },
];

const STORY = [
  {
    title: 'The Problem',
    desc: "Nepal's government and businesses are ready to adopt AI — but almost all available tools are built for English, Hindi, or Mandarin. Nepali is a 'low-resource' language that global AI companies largely ignore. Organizations end up adapting foreign tools that misunderstand Nepali grammar, fail on Devanagari OCR, or produce translations full of errors.",
  },
  {
    title: 'Our Answer',
    desc: 'Uttar Labs builds AI from the ground up for Nepali. Our NLP models are trained on Nepali corpora. Our OCR is optimized for Devanagari script. Our chatbots understand Nepali idiom and bureaucratic language. We do not retrofit — we build right.',
  },
  {
    title: 'The Opportunity',
    desc: "Nepal's National AI Policy 2082 (August 2025) mandates AI integration across public services. The government is actively seeking Nepali AI partners. The global NLP market is projected to grow from $70B to $250B by 2031. Nepal's first-mover in Nepali NLP has an extraordinary window.",
  },
];

export function AboutPage() {
  const navigate = useNavigate();

  return (
    <div className="tab-content">
      {/* Hero */}
      <section
        className="civic-hero"
        style={{
          background: `linear-gradient(135deg, rgba(10,24,50,0.90) 0%, rgba(8,50,80,0.85) 100%), url(${P(6845912)})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="civic-hero-inner">
          <div className="civic-hero-badge">Founded in Kathmandu · Built for Nepal</div>
          <h1 className="civic-hero-title">About Uttar Labs</h1>
          <p className="civic-hero-subtitle">
            We are an AI & NLP company dedicated to making artificial intelligence work for Nepal — in Nepali, for Nepali organizations, and at prices Nepal can afford.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="section">
        <div className="card" style={{ background: 'linear-gradient(135deg, #0f4c81 0%, #0891b2 100%)', color: 'white' }}>
          <h2 style={{ color: 'white', marginBottom: '1rem', fontSize: '1.8rem' }}>Our Mission</h2>
          <p style={{ fontSize: '1.15rem', opacity: 0.95, lineHeight: 1.75, maxWidth: 760 }}>
            To make AI accessible, affordable, and contextually relevant for Nepali government agencies and businesses — so the benefits of the AI revolution reach Nepal's 30 million people, not just the global tech elite.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="section">
        <h2 className="section-title">Why We Started</h2>
        <div className="cards-grid">
          {STORY.map(s => (
            <div key={s.title} className="card">
              <h3 className="card-title" style={{ fontSize: '1.1rem' }}>{s.title}</h3>
              <p className="card-description">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="section">
        <h2 className="section-title">Our Values</h2>
        <div className="cards-grid">
          {VALUES.map(v => (
            <div key={v.title} className="card" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>{v.icon}</div>
              <h3 className="card-title">{v.title}</h3>
              <p className="card-description">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Team */}
      <section className="section">
        <h2 className="section-title">The Team</h2>
        <p className="section-subtitle">Built by Nepali engineers, for Nepal.</p>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div className="card" style={{ maxWidth: 340, textAlign: 'center' }}>
            <div style={{
              width: 80, height: 80, borderRadius: '50%',
              background: 'linear-gradient(135deg, #0f4c81 0%, #0891b2 100%)',
              color: 'white', fontSize: '1.5rem', fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 1rem',
            }}>
              GG
            </div>
            <h3 className="card-title" style={{ fontSize: '1.1rem' }}>Govinda Ghimire</h3>
            <p style={{ color: '#0891b2', fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.75rem' }}>
              Founder & Lead AI Engineer
            </p>
            <p className="card-description">
              AI/NLP engineer with expertise in low-resource language models and Nepal's digital transformation landscape.
            </p>
          </div>
        </div>
      </section>

      {/* Nepal AI context */}
      <section className="section">
        <h2 className="section-title">Nepal's AI Moment</h2>
        <div className="timeline">
          {[
            { year: '2025', title: "National AI Policy 2082 Approved", desc: "Nepal's government officially approved the National AI Policy in August 2025, mandating AI integration across public services and creating the framework for government AI contracts." },
            { year: '2026', title: 'Digital Transformation Acceleration', desc: 'Government agencies begin actively procuring AI solutions. Banks, hospitals, and agritech platforms race to deploy Nepali-language AI. Uttar Labs launches to fill the Nepali NLP gap.' },
            { year: '2028', title: 'Nepal as a Regional NLP Hub', desc: 'Nepal builds a reputation for South Asian language AI expertise — serving the Nepali diaspora globally and exporting NLP solutions to neighboring low-resource language markets.' },
          ].map(item => (
            <div key={item.year} className="timeline-item">
              <div className="timeline-content">
                <div className="timeline-year">{item.year}</div>
                <div className="timeline-title">{item.title}</div>
                <div className="timeline-description">{item.desc}</div>
              </div>
              <div className="timeline-dot" />
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="card" style={{ background: 'linear-gradient(135deg, #0f4c81 0%, #0891b2 100%)', color: 'white', textAlign: 'center' }}>
          <h2 style={{ color: 'white', marginBottom: '0.75rem' }}>We're building Nepal's AI foundation</h2>
          <p style={{ opacity: 0.9, marginBottom: '1.5rem', maxWidth: 520, margin: '0 auto 1.5rem' }}>
            Whether you want to work with us, partner with us, or explore what we can do for your organization — let's start a conversation.
          </p>
          <button
            onClick={() => navigate('/contact')}
            style={{ padding: '0.875rem 2rem', background: 'white', color: '#0f4c81', border: 'none', borderRadius: '25px', fontWeight: 700, cursor: 'pointer', fontSize: '1rem' }}
          >
            Get in Touch
          </button>
        </div>
      </section>
    </div>
  );
}
